import Phaser from "phaser";
import { AssetKeys } from "../assets";
import { WOLF_FEEL_CONFIG } from "../wolfFeelConfig";
import type { WolfMovementStateId } from "../wolfRealismConfig";
import { USE_WOLF_V2, WOLF_V2_ANIM_MAP } from "../wolfV2Config";

// ─── Static Animation Config ─────────────────────────────────────────────────
export const WOLF_ANIMATION_CONFIG = {
  frameWidth: 512,
  frameHeight: 512,
  originX: 0.5,
  originY: 0.55,
  baseScale: 0.34,
  runFps: WOLF_FEEL_CONFIG.animationFps,
  idleFps: 6,
  turnFps: 16,
  dashFps: 18,
  injuredFps: 10,
  championFps: 16,
  diagonalThreshold: 0.25,
  turnThreshold: 0.55,
  auraOpacity: 0.55,
  eyeGlowOpacity: 0.75,
  trailOpacity: 0.5,
  maxSteerRotation: Phaser.Math.DegToRad(WOLF_FEEL_CONFIG.rotationMaxDegrees)
} as const;

// Visual test toggle for the wolf's green face accents: the two eye dots
// (drawEyeGlow) and the "V"/chevron (drawAccents). Purely decorative — the wolf's
// upward orientation is carried by the sprite itself, not these graphics.
// Set to `true` to restore them; no code was removed, so reverting is one flag.
export const SHOW_WOLF_FACE_ACCENTS = false;

type WolfAnimationState = "idle" | "run" | "champion" | "dash" | "injured";

export interface WolfAnimationUpdate {
  dt: number;
  inputX: number;
  moving: boolean;
  speedRatio: number;
  champion: boolean;
  movementState?: WolfMovementStateId;
  rotationLerp?: number;
  rotationMaxDegrees?: number;
  animationFpsMultiplier?: number;
}

// ─── Modular V2 Animation Resolver ───────────────────────────────────────────
/**
 * Returns the best available animation key for the requested logical state.
 *
 * Resolution order:
 *   1. V2 key, if USE_WOLF_V2 is true AND the animation has been registered
 *      in the Phaser animation manager (i.e. the asset was loaded and passed
 *      validation in BootScene).
 *   2. V1 fallback key from WOLF_V2_ANIM_MAP.
 *
 * Adding a new V2 animation only requires:
 *   a) Adding an entry to WOLF_V2_ANIM_MAP in wolfV2Config.ts.
 *   b) Loading and registering it in BootScene.ts.
 *   No further changes to WolfAnimationSystem are needed.
 */
function resolveAnimKey(
  scene: Phaser.Scene,
  state: keyof typeof WOLF_V2_ANIM_MAP
): string {
  if (!USE_WOLF_V2) return WOLF_V2_ANIM_MAP[state].v1Key;
  const entry = WOLF_V2_ANIM_MAP[state];
  if (entry.frameCount > 0 && scene.anims.exists(entry.v2Key)) return entry.v2Key;
  return entry.v1Key;
}

// ─── WolfAnimationSystem ─────────────────────────────────────────────────────
export class WolfAnimationSystem {
  readonly container: Phaser.GameObjects.Container;
  readonly bodyDisplaySize = Math.round(WOLF_ANIMATION_CONFIG.frameWidth * WOLF_ANIMATION_CONFIG.baseScale);

  private shadowSoft: Phaser.GameObjects.Ellipse;
  private shadow: Phaser.GameObjects.Ellipse;
  private trail: Phaser.GameObjects.Graphics;
  private body: Phaser.GameObjects.Sprite;
  private eyeGlow: Phaser.GameObjects.Graphics;
  private aura: Phaser.GameObjects.Image;
  private accents: Phaser.GameObjects.Graphics;
  private state: WolfAnimationState = "idle";
  private steerRotation = 0;
  private championVisual = false;
  private lastAnimKey: string | null = null;

  constructor(private scene: Phaser.Scene, layer: Phaser.GameObjects.Layer) {
    const shadowWidth = 118 * WOLF_FEEL_CONFIG.shadowScale;
    const shadowHeight = 36 * WOLF_FEEL_CONFIG.shadowScale;
    this.shadowSoft = scene.add.ellipse(
      0,
      WOLF_FEEL_CONFIG.shadowOffsetY,
      shadowWidth * 1.18,
      shadowHeight * 1.34,
      0x000000,
      WOLF_FEEL_CONFIG.shadowOpacity * 0.28
    );
    this.shadow = scene.add.ellipse(0, WOLF_FEEL_CONFIG.shadowOffsetY, shadowWidth, shadowHeight, 0x000000, WOLF_FEEL_CONFIG.shadowOpacity);
    this.trail = scene.add.graphics();
    this.body = scene.add
      .sprite(0, 0, AssetKeys.wolf.idle)
      .setOrigin(WOLF_ANIMATION_CONFIG.originX, WOLF_ANIMATION_CONFIG.originY)
      .setDisplaySize(this.bodyDisplaySize, this.bodyDisplaySize)
      .setAlpha(1);
    this.eyeGlow = scene.add.graphics();
    this.aura = scene.add
      .image(0, 0, AssetKeys.vfx.wolfGlow)
      .setDisplaySize(254, 254)
      .setAlpha(0)
      .setTint(0xdfff2f)
      .setBlendMode(Phaser.BlendModes.ADD);
    this.accents = scene.add.graphics();
    this.container = scene.add.container(0, 0, [this.shadowSoft, this.shadow, this.trail, this.body, this.eyeGlow, this.aura, this.accents]);
    this.container.setDepth(1000);
    layer.add(this.container);
  }

  reset(x: number, y: number) {
    this.state = "idle";
    this.steerRotation = 0;
    this.championVisual = false;
    this.lastAnimKey = null;
    this.container.setPosition(x, y).setRotation(0);
    this.shadowSoft.setRotation(0);
    this.shadow.setRotation(0);
    // Stop the carried-over animation so the new run's play(animKey, true) is not
    // skipped by ignoreIfPlaying. Without this, the run-2+ body keeps the V2 256px
    // frames while the scale stays frozen on the 512px idle frame, shrinking the
    // wolf to ~87px. Stopping forces a clean play() that re-syncs frame and scale.
    this.body.stop();
    this.body.setTexture(AssetKeys.wolf.idle).setDisplaySize(this.bodyDisplaySize, this.bodyDisplaySize);
    this.body.anims.timeScale = 1;
    this.aura.setAlpha(0);
    this.trail.clear();
    this.eyeGlow.clear();
    this.accents.clear();
  }

  setPosition(x: number, y: number) {
    this.container.setPosition(x, y);
  }

  setPaused(paused: boolean) {
    if (paused) {
      this.body.anims.pause();
    } else {
      this.body.anims.resume();
    }
  }

  update({ dt, inputX, moving, speedRatio, champion, movementState = "longChase", rotationLerp = WOLF_FEEL_CONFIG.rotationLerp, rotationMaxDegrees = WOLF_FEEL_CONFIG.rotationMaxDegrees, animationFpsMultiplier = 1 }: WolfAnimationUpdate) {
    this.state = champion ? "champion" : moving ? "run" : "idle";
    const targetRotation = Phaser.Math.Clamp(inputX, -1, 1) * Phaser.Math.DegToRad(rotationMaxDegrees);
    this.steerRotation = Phaser.Math.Linear(this.steerRotation, targetRotation, Math.min(1, dt * rotationLerp));
    this.container.setRotation(this.steerRotation);
    this.shadowSoft.setRotation(-this.steerRotation);
    this.shadow.setRotation(-this.steerRotation);

    const animKey = this.getBodyAnimationKey(champion);
    const useIdleTexture = this.state === "idle" && !champion && animKey === "wolf-run";
    if (useIdleTexture) {
      this.body.stop();
      this.body.setTexture(AssetKeys.wolf.idle);
    } else {
      this.body.play(animKey, true);
      this.body.anims.timeScale = animationFpsMultiplier;
    }

    // Each sprite sheet has a different native frame size (idle/champion 512px,
    // V2 run grid 256px). setDisplaySize fixes scale against the *current* frame,
    // so it must be re-applied whenever the active animation/texture changes —
    // otherwise the scale stays frozen on the construction frame and the wolf
    // renders at the wrong on-screen size (the V2 run sheet halved it to ~87px).
    // Re-applying keeps every state at a constant bodyDisplaySize (174px), so the
    // normal wolf matches the champion. Guarded by lastAnimKey to avoid per-frame work.
    const effectiveKey = useIdleTexture ? "__idle_texture__" : animKey;
    if (effectiveKey !== this.lastAnimKey) {
      this.lastAnimKey = effectiveKey;
      this.body.setDisplaySize(this.bodyDisplaySize, this.bodyDisplaySize);
    }

    this.body.setAlpha(1);
    this.applyChampionVisual(champion);

    this.drawEyeGlow(champion);
    this.drawAccents(champion);
    this.drawTrail(speedRatio, champion, movementState);
  }

  private drawEyeGlow(champion: boolean) {
    this.eyeGlow.clear();
    if (!SHOW_WOLF_FACE_ACCENTS) return;
    const alpha = champion ? WOLF_ANIMATION_CONFIG.eyeGlowOpacity : WOLF_ANIMATION_CONFIG.eyeGlowOpacity * 0.38;
    this.eyeGlow.fillStyle(champion ? 0xdfff2f : 0xb7ff00, alpha);
    this.eyeGlow.fillCircle(-8, -52, champion ? 4 : 2.6);
    this.eyeGlow.fillCircle(8, -52, champion ? 4 : 2.6);
  }

  private drawAccents(champion: boolean) {
    this.accents.clear();
    if (!SHOW_WOLF_FACE_ACCENTS) return;
    this.accents.lineStyle(2, champion ? 0xdfff2f : 0xb7ff00, champion ? 0.72 : 0.32);
    this.accents.lineBetween(-14, 32, 0, 70);
    this.accents.lineBetween(14, 32, 0, 70);
  }

  private drawTrail(speedRatio: number, champion: boolean, movementState: WolfMovementStateId) {
    this.trail.clear();
    const sprintBoost = movementState === "sprint" ? 0.08 : 0;
    const alpha = Phaser.Math.Clamp((speedRatio - 0.62) * 0.28, 0, 0.16) + (champion ? 0.12 : 0) + sprintBoost;
    if (alpha <= 0.01) return;
    this.trail.fillStyle(champion ? 0xdfff2f : 0xffffff, alpha);
    this.trail.fillEllipse(0, 58, movementState === "sprint" ? 72 : champion ? 82 : 56, movementState === "sprint" ? 128 : champion ? 132 : 92);
  }

  private applyChampionVisual(champion: boolean) {
    if (this.championVisual === champion) return;
    this.championVisual = champion;
    this.aura.setAlpha(champion ? WOLF_ANIMATION_CONFIG.auraOpacity : 0);
    this.aura.setDisplaySize(champion ? 274 : 254, champion ? 274 : 254);
  }

  /**
   * Resolves the best available Phaser animation key for the current logical
   * state using the modular V2 map. Falls back to V1 automatically when the
   * V2 asset is not loaded or not yet integrated (frameCount === 0).
   */
  private getBodyAnimationKey(champion: boolean): string {
    if (champion) return resolveAnimKey(this.scene, "champion");
    return resolveAnimKey(this.scene, "run");
  }
}
